import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { some, get, isFunction, isEmpty } from 'lodash'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import cn from 'classnames'

import HamburgerIcon from './HamburgerIcon'
import Avatar from './Avatar'

import styles from './NavMenu.module.scss'

import { networkConnected } from '../utils/networkConnected'

import { getDeviceSubtype } from '../selectors/config'
import { getVenueName } from '../selectors/venue'
import { hasCartItems } from '../selectors/cart'
import DeviceSubtypes from '../utils/deviceSubtypes'

import { logout } from '../actions/attendant'
import { getAttendants, makeGetActiveAttendant } from '../selectors/attendant'
import { refreshConfig } from '../actions/config'
import Button from './Button'
import ConfirmNotification from './ConfirmNotification'

const activeLinkForRoute = (linkName, pathname) => {
  const ACTIVE_PATH_MAP = {
    tabs: [
      /^\/tabs/, /^\/orders/,
    ],
    orders: [
      /^\/tabs/, /^\/orders/,
    ],
    menu: [
      /^\/concession-order/
    ],
    debugInfo: [
      /^\/debug-info/
    ]
  }

  const targetRegularExpressions = ACTIVE_PATH_MAP[linkName]
  const isActive = some(targetRegularExpressions, (regex) => {
    return regex.test(pathname)
  })

  return isActive
}

const NavMenu = ({ history, venueName, deviceSubtype, onToggle, attendantName, refreshConfig, logout, cartItemsExist, attendantsExist, className }) => {
  const animationLength = 350
  const [animationState, setAnimationState] = useState('in')
  const [confirmLogout, setConfirmLogout] = useState(false)
  const [toastMessage, setToastMessage] = useState({ show: false, id: null })
  const classNames = cn(styles.navMenu, className)

  let logoutContent = (
    <>
      <Avatar className={styles.avatar} />
      <span className={styles.name}>{attendantName}</span>
      <span className={styles.logoutText}>{attendantName ? 'Logout' : 'Login'}</span>
    </>
  )

  const handleOnToggle = (callback) => {
    setAnimationState('out')

    setTimeout(() => {
      if (isFunction(callback)) {
        callback()
      }

      // eslint-disable-next-line no-unused-expressions
      onToggle?.()
    }, animationLength)
  }

  const onClickMenu = () => {
    handleOnToggle(() => {
      history.push('/concession-order')
    })
  }

  const onClickOrders = () => {
    handleOnToggle(() => {
      DeviceSubtypes.isKiosk(deviceSubtype)
      ? history.push('/orders/menuSelection')
      : history.push('/orders')
    })
  }

  const onClickTabs = () => {
    handleOnToggle(() => {
      history.push('/tabs')
    })
  }

  const onClickConfiguration = () => {
    handleOnToggle(() => {
      let historyState = undefined

      if (attendantsExist && !attendantName) {
        historyState = { redirectTo: '/user-login' }
      }

      history.push('/revenue-center-setup', historyState)
    })
  }

  const onClickRefreshConfig = () => {
    handleOnToggle(() => {
      refreshConfig()
    })
  }

  const toggleMessages = () => {
    toast.dismiss(toastMessage.id)
    setToastMessage({ show: false, id: null })
  }

  const checkCartBeforeConfirm = (onConfirm) => {
    const notification = (
      <ConfirmNotification
        className={className}
        label={'Warning'}
        message={
          'Items currently in cart will be removed upon syncing or navigating away from the menu. Do you wish to continue?'
        }
        onCancel={toggleMessages}
        onConfirm={onConfirm}
        buttonOneText={'Cancel'}
        buttonTwoText={'Confirm Cart Abandonment'}
      />
    )

    if (cartItemsExist) {
      warnNotification(notification)
    } else {
      onConfirm()
    }
  }

  const refreshCache = () => {
    networkConnected().then(() => {
      window.location = `${window.location.origin}/tabs?cacheBuster=${new Date().toISOString()}`
    })
  }

  const warnNotification = (notification) => {
    const toastId = toast(notification, { autoClose: false, closeOnClick: false })
    setToastMessage({ show: true, id: toastId })
  }

  const onClickDebugInfo = () => {
    handleOnToggle(() => {
      history.replace('/debug-info')
    })
  }

  const onClickKiosk = () => {
    handleOnToggle(() => {
      history.replace('/kiosk/attract_loop')
    })
  }

  const onClickLogout = () => {
    handleOnToggle(() => {
      logout()
      history.replace('/user-login')
    })
  }

  const handleLoginLogout = () => {
    if (!confirmLogout && attendantName) {
      setConfirmLogout(true)
    } else if (!attendantName) {
      handleOnToggle(() => {
        logout()
        history.replace('/user-login')
      })
    }
  }

  if (confirmLogout) {
    logoutContent = (
      <div className={styles.logoutConfirmation}>
        <p>Are you sure you want to logout?</p>
        <Button onClick={() => setConfirmLogout(false)}>No</Button>
        <Button className={styles.confirmLogoutButton} onClick={onClickLogout}>YES</Button>
      </div>
    )
  }

  let logoutContainer = null
  const { isSuites, isTabs } = DeviceSubtypes

  if (isSuites(deviceSubtype) || (isTabs(deviceSubtype) && attendantsExist)) {
    logoutContainer = (
      <div className={styles.logout} onClick={handleLoginLogout}>
        {logoutContent}
      </div>
    )
  }

  useEffect(() => {
    setAnimationState(undefined)
  }, [])

  return (
    <div className={cn(classNames, { [styles[animationState]]: animationState !== undefined  })}>
      <div className={styles.sidebarContainer}>
        <div className={styles.header}>
          <div>{DeviceSubtypes.isSuites(deviceSubtype) && venueName}</div>
          <button className={styles.menuToggle} onClick={handleOnToggle}>
            <HamburgerIcon className={styles.menuToggleIcon} />
          </button>
        </div>
        <div className={styles.links}>
          {DeviceSubtypes.isConcessions(deviceSubtype) && <div className={cn({ [styles.active]: activeLinkForRoute('menu', history.location.pathname) })} onClick={onClickMenu} title='Menu'><span>Menu</span></div>}
          {DeviceSubtypes.isKiosk(deviceSubtype) && <div onClick={onClickKiosk} title='Kiosk'><span>Kiosk</span></div>}

          {DeviceSubtypes.isTabs(deviceSubtype) && <div className={cn({ [styles.active]: activeLinkForRoute('tabs', history.location.pathname) })} onClick={() => checkCartBeforeConfirm(onClickTabs)} title='Tabs'><span>Tabs</span></div>}
          {DeviceSubtypes.isSuites(deviceSubtype) && <div className={cn({ [styles.active]: activeLinkForRoute('orders', history.location.pathname) })} onClick={() => checkCartBeforeConfirm(onClickTabs)} title='Orders'><span>Orders</span></div>}
          {DeviceSubtypes.isConcessions(deviceSubtype) && <div className={cn({ [styles.active]: activeLinkForRoute('orders', history.location.pathname) })} onClick={onClickOrders} title='Orders'><span>Orders</span></div>}

          <div onClick={() => checkCartBeforeConfirm(onClickConfiguration)} title='Configuration'><span>Configuration</span></div>
          <div onClick={() => checkCartBeforeConfirm(refreshCache)} title='Refresh'><span>Sync Network Data</span></div>
          <div onClick={() => checkCartBeforeConfirm(onClickRefreshConfig)} title='Refresh'><span>Refresh Config</span></div>
          <div className={cn({ [styles.active]: activeLinkForRoute('debugInfo', history.location.pathname) })} onClick={() => checkCartBeforeConfirm(onClickDebugInfo)} title='Debug Info'><span>Debug Info</span></div>
        </div>
        {logoutContainer}
      </div>
      <div className={styles.overlay} onClick={handleOnToggle} />
    </div>
  )
}

NavMenu.propTypes = {
  onToggle: PropTypes.func,
}

const mapStateToProps = (state) => {
  const getActiveAttendant = makeGetActiveAttendant()
  const deviceSubtype = getDeviceSubtype(state)
  const activeAttendant = getActiveAttendant(state)
  const venueName = getVenueName(state)
  const cartItemsExist = hasCartItems(state)
  const attendantsExist = !isEmpty(getAttendants(state))

  return {
    deviceSubtype,
    attendantName: get(activeAttendant, 'name', ''),
    venueName,
    cartItemsExist,
    attendantsExist,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(logout()),
    refreshConfig: () => dispatch(refreshConfig()),
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(NavMenu))
